import { forwardRef, type Ref } from "react";
import { styled } from "@pigment-css/react";
import { ChevronDownWide } from "@acto/web-common/components";
import { useLocale } from "@acto/web-common/locales";
import type { Customer, Meeting } from "@acto/taskpane/core/domain";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { SummaryText } from "@acto/taskpane/field-sales-2/components/primitives/SummaryText";
import { formatCustomerAddress } from "@acto/taskpane/field-sales-2/utils/format";

interface CustomerCardProps {
  meeting: Meeting;
  // Richer client data (tags + detail fields). When present it supplies the detail
  // rows and tags; the header/date always come from `meeting`. Lets the card show
  // thin data immediately and upgrade in place once the full fetch resolves.
  detailCustomer?: Customer;
  // Suppress the date badge — used for ad-hoc recaps that reference no meeting, so the
  // synthetic meeting's placeholder date is never shown.
  hideDate?: boolean;
  showBadgeTime?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
  // Replaces the expanded detail rows/tags with this text and keeps the card open.
  summary?: string | null;
  className?: string;
}

// The card's visual surface (background/border/radius/shadow) lives here only, so the
// blank behind-card placeholders in the deck share the exact same surface tokens.
export const CardSurface = styled("div")({
  background: "#f8f8f7",
  border: "1px solid rgba(255, 255, 255, 0.64)",
  borderRadius: 36,
  boxShadow: "0px 39px 39px rgba(0,0,0,0.02), 0px 88px 53px rgba(0,0,0,0.01)",
});

const Root = styled(CardSurface)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  boxSizing: "border-box",
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[24]}`,
}));

const Header = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.fieldSalesTwo.spacing[16],
  width: "100%",
}));

const DateBadge = styled("div")(({ theme }) => ({
  flexShrink: 0,
  width: 56,
  height: 60,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.fieldSalesTwo.spacing[4],
  borderRadius: theme.fieldSalesTwo.radius[18],
  background: "rgba(19, 19, 19, 0.04)",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[13],
  lineHeight: 1.32,
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.wide,
  textAlign: "center",
}));

const BadgeDate = styled("span")(({ theme }) => ({
  fontWeight: theme.fieldSalesTwo.font.weight[540],
  color: "rgba(19, 19, 19, 0.9)",
}));

const BadgeTime = styled("span")(({ theme }) => ({
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[800],
}));

const TitleBlock = styled("div")(({ theme }) => ({
  flex: "1 0 0",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[6],
  paddingTop: theme.fieldSalesTwo.spacing[4],
}));

const Title = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[22],
  fontWeight: theme.fieldSalesTwo.font.weight[520],
  lineHeight: 1.36,
  letterSpacing: "-0.2px",
  color: theme.fieldSalesTwo.colors.neutral[900],
  // Long customer names (e.g. long German org names) must stay inside the card: break within a
  // word if needed, then clamp to 3 lines. `minWidth: 0` on the flex parent lets the box shrink,
  // but only overflow-wrap breaks the word itself (ACT2-2943).
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 3,
  overflow: "hidden",
  overflowWrap: "break-word",
}));

const RefText = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[14],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  lineHeight: 1.5,
  color: "rgba(19, 19, 19, 0.72)",
}));

// Accordion: animate height via grid-template-rows 0fr ↔ 1fr (no JS measuring).
// Always mounted so collapsing animates too; the inner clips via overflow + min-height 0.
const DetailsWrap = styled("div")<{ open: boolean }>(({ theme }) => ({
  display: "grid",
  gridTemplateRows: "0fr",
  opacity: 0,
  transition: `grid-template-rows ${theme.fieldSalesTwo.motion.duration.slow} ${theme.fieldSalesTwo.motion.easing.emphasized}, opacity ${theme.fieldSalesTwo.motion.duration.slow} ${theme.fieldSalesTwo.motion.easing.standard}`,
  variants: [{ props: { open: true }, style: { gridTemplateRows: "1fr", opacity: 1 } }],
}));

const Details = styled("dl")(({ theme }) => ({
  margin: 0,
  minHeight: 0,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[16],
  paddingTop: theme.fieldSalesTwo.spacing[24], // the gap below the header, clipped while collapsed
}));

const DetailRow = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: theme.fieldSalesTwo.spacing[6],
}));

const DetailLabel = styled("dt")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[700],
  overflowWrap: "anywhere",
}));

const DetailValue = styled("dd")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[15],
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflowWrap: "anywhere",
}));

const ExpandButton = styled("button")(({ theme }) => ({
  alignSelf: "center",
  marginTop: theme.fieldSalesTwo.spacing[24], // constant gap above, regardless of accordion state
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: theme.fieldSalesTwo.spacing[48],
  height: theme.fieldSalesTwo.spacing[24],
  padding: 0,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: theme.fieldSalesTwo.colors.neutral[700],
}));

const Chevron = styled(ChevronDownWide)<{ open: boolean }>({
  width: 17,
  height: 6,
  color: "rgba(19, 19, 19, 0.64)",
  transition: "transform 180ms cubic-bezier(0.22, 1, 0.36, 1)",
  variants: [{ props: { open: true }, style: { transform: "rotate(180deg)" } }],
});

export const CustomerCard = forwardRef<HTMLElement, CustomerCardProps>(function CustomerCard(
  {
    meeting,
    detailCustomer,
    hideDate = false,
    showBadgeTime = false,
    expanded = false,
    onToggleExpand,
    summary,
    className,
  },
  ref,
) {
  const { locale } = useLocale();
  // Header/date come from the meeting; detail rows + tags prefer the richer fetched
  // customer, falling back to the thin meeting customer until it arrives.
  const customer = meeting.customer;
  const detail = detailCustomer ?? customer;
  // Custom tags render as labeled key-value rows (ACT2-2931), consistent with the base
  // detail rows. A tag needs both a translated name (the label) and a value to be
  // a meaningful row — same gate as the customer info page (CustomerInfo.tsx).
  const tagRows = (detail.clientTags ?? []).flatMap((tag) =>
    tag.name.hasTranslations() && tag.value
      ? [{ key: `tag-${tag.key}`, label: tag.name.getTranslation(locale), value: tag.value }]
      : [],
  );

  const baseRows = [
    {
      key: "Addresse",
      label: "Addresse",
      value: formatCustomerAddress(detail) ?? "",
    },
    { key: "Kundennummer", label: "Kundennummer", value: detail.externalReference ?? "" },
    { key: "Kategorie", label: "Kategorie", value: detail.industry ?? "" },
    { key: "Segment", label: "Segment", value: detail.group ?? "" },
    { key: "Typ", label: "Typ", value: detail.branch ?? "" },
    { key: "Region", label: "Region", value: detail.region ?? "" },
  ].filter((row) => row.value);

  // Base detail rows first, then custom tags — mirrors the row order of the customer
  // detail page (CustomerInfo) so the two views read consistently (ACT2-2931).
  const rows = [...baseRows, ...tagRows];

  return (
    <Root ref={ref as Ref<HTMLDivElement>} className={className}>
      <Header>
        {!hideDate && (
          <DateBadge>
            <BadgeDate>{meeting.scheduledDate.format("DD.MM")}</BadgeDate>
            {showBadgeTime && meeting.scheduledStartTime && (
              <BadgeTime>{meeting.scheduledStartTime.slice(0, 5)}</BadgeTime>
            )}
          </DateBadge>
        )}
        <TitleBlock>
          <Title>{customer.name}</Title>
          <RefText>{customer.externalReference}</RefText>
        </TitleBlock>
      </Header>

      <DetailsWrap open={expanded || !!summary}>
        {summary ? (
          // as="div": a <p> isn't a valid <dl> child.
          <Details as="div">
            <Divider />
            <SummaryText>{summary}</SummaryText>
          </Details>
        ) : (
          <Details>
            <Divider />
            {rows.map((row) => (
              <DetailRow key={row.key}>
                <DetailLabel>{row.label}</DetailLabel>
                <DetailValue>{row.value}</DetailValue>
              </DetailRow>
            ))}
          </Details>
        )}
      </DetailsWrap>

      {/* No custom tags → nothing worth expanding, so hide the toggle entirely (ACT2-2912). */}
      {onToggleExpand && tagRows.length > 0 && (
        <ExpandButton type="button" aria-label="Details" aria-expanded={expanded} onClick={onToggleExpand}>
          <Chevron open={expanded} />
        </ExpandButton>
      )}
    </Root>
  );
});
